import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Paper, Container, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";

// The environment-specific password is set in Vercel's environment variables
const SITE_PASSWORD = import.meta.env.VITE_SITE_PASSWORD;
const USE_PASSWORD = import.meta.env.VITE_USE_PASSWORD !== 'false';

// Password must be set via VITE_SITE_PASSWORD env variable
// No hardcoded default - if USE_PASSWORD is true but no password is configured, access is blocked

interface PasswordProtectProps {
  children: React.ReactNode;
}

const PasswordProtect: React.FC<PasswordProtectProps> = ({ children }) => {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  // Check if the user is already authenticated (from localStorage)
  useEffect(() => {
    // If password protection is disabled, auto-authenticate
    if (!USE_PASSWORD) {
      setIsAuthenticated(true);
      return;
    }

    const authenticated = localStorage.getItem("dbr-rfq-authenticated");
    if (authenticated === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // If no password is set in environment variables, block access
    const correctPassword = SITE_PASSWORD;
    if (!correctPassword) {
      setError("Site password not configured. Set VITE_SITE_PASSWORD environment variable.");
      return;
    }

    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError(null);
      localStorage.setItem("dbr-rfq-authenticated", "true");
    } else {
      setError("Incorrect password. Please try again.");
      setAttempts((prev) => prev + 1);
    }
  };

  // If too many attempts, add a slight delay to prevent brute forcing
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null); // Clear error when user types
    if (attempts > 5) {
      setTimeout(() => {
        setPassword(e.target.value);
      }, 500);
    } else {
      setPassword(e.target.value);
    }
  };

  // If password protection is disabled or user is authenticated, render the children
  if (!USE_PASSWORD || isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, render the login form
  return (
    <Container
      maxWidth="xs"
      sx={{ minHeight: "100vh", py: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Paper
        elevation={1}
        sx={{
          p: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", flexDirection: "column" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              mb: 1.5,
            }}
          >
            <LockIcon color="primary" />
          </Box>
          <Typography variant="h5" component="h1" gutterBottom>
            DBR RFQ System
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This site is password protected.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            margin="normal"
            autoFocus
            required
            error={!!error}
            inputProps={{
              autoComplete: "current-password"
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ mt: 2 }}
          >
            Access System
          </Button>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              mt: 4, 
              textAlign: 'center', 
              width: '100%',
              display: 'block'
            }}
          >
            DBR RFQ System • Secured Access • 2025
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordProtect;
